/* ============================================================================
 * BeyondCarat Pricing Core — SINGLE SOURCE OF TRUTH
 *
 * THIS IS THE CANONICAL PRICING LOGIC. Edit only this file.
 * Run `npm run sync-pricing` (from this repo root) to copy into:
 *   - assets/pricing-core.js                                             (theme — browser global)
 *   - shopify-functions/beyondcarat-pricing/extensions/diamond-pricing/src/pricing-core.js  (cart-transform)
 *   - ../BeyonCaratUploader/bc-uploader-web/src/lib/pricing-core.js     (Google Feed)
 *
 * Constraints (must run in the Shopify Function sandbox):
 *   - Pure functions only — no I/O, no fetch, no Date.now (today is a parameter)
 *   - ES2020 syntax only
 *   - No external dependencies
 *   - No `export` keywords (the sync script appends target-appropriate footers)
 *
 * Resolution order matches snippets/diamond-customizer.liquid (lines 71-79)
 * AND shopify-functions/.../cart_transform_run.js (resolveMatrix):
 *   1. Active matrix campaign (first whose [starts_at, ends_at] window contains today)
 *   2. product.metafields.custom.pricing_overrides.price_matrix_override
 *   3. shop.metafields.custom.pricing_config.price_lists[product.price_list || 'solitaire_rings']
 * Then any active discount campaign overlays a multiplier on (variant + stone).
 * ========================================================================== */

/**
 * Extract YYYY-MM-DD prefix from an ISO-ish date string.
 * @param {string|null|undefined} s
 * @returns {string}
 */
function dateOnly(s) {
  return (s || '').substring(0, 10);
}

/**
 * Find first campaign whose [starts_at, ends_at] window contains todayDate.
 * Lexical YYYY-MM-DD comparison (time-of-day ignored).
 * @param {Array<Object>|null|undefined} campaigns
 * @param {string} todayDate "YYYY-MM-DD"
 * @returns {Object|null}
 */
function findActiveCampaign(campaigns, todayDate) {
  if (!Array.isArray(campaigns) || campaigns.length === 0 || !todayDate) return null;
  for (let i = 0; i < campaigns.length; i++) {
    const c = campaigns[i];
    if (!c || typeof c !== 'object') continue;
    const starts = dateOnly(c.starts_at);
    const ends = dateOnly(c.ends_at);
    if (!starts || !ends) continue;
    if (todayDate >= starts && todayDate <= ends) return c;
  }
  return null;
}

/**
 * Resolve the base matrix (before any campaign overlay).
 * @param {{
 *   pricingConfig?: { price_lists?: Object } | null,
 *   productOverrides?: { price_matrix_override?: Object } | null,
 *   priceListName?: string | null
 * }} args
 * @returns {{ matrix: Object|null, source: string }}
 */
function resolveBaseMatrix(args) {
  const overrides = (args && args.productOverrides) || null;
  const pricingConfig = (args && args.pricingConfig) || null;

  if (overrides && overrides.price_matrix_override && typeof overrides.price_matrix_override === 'object') {
    return { matrix: overrides.price_matrix_override, source: 'product_override', defaulted: false };
  }

  // `defaulted` = no explicit price_list was supplied, so we fell back to the
  // solitaire_rings matrix. Display + feed runtimes keep using the fallback matrix
  // (so a price still renders), but the cart-transform money path treats a defaulted
  // resolution as NON-authoritative and charges the customer's displayed price
  // instead of guessing with the ring matrix — which would mis-price earrings.
  const explicitName = (args && args.priceListName) || '';
  const defaulted = !explicitName;
  const name = explicitName || 'solitaire_rings';
  const lists = pricingConfig && pricingConfig.price_lists;
  if (lists && lists[name] && typeof lists[name] === 'object') {
    return { matrix: lists[name], source: 'price_list:' + name + (defaulted ? ':defaulted' : ''), defaulted: defaulted };
  }
  return { matrix: null, source: 'price_list:' + name + ':missing', defaulted: defaulted };
}

/**
 * Full resolution: base matrix + active campaign overlay.
 * Output mirrors what assets/diamond-customizer.js applies after loadPriceMatrix + loadProductCampaigns.
 *
 * @param {{
 *   pricingConfig?: Object | null,
 *   productOverrides?: Object | null,
 *   priceListName?: string | null,
 *   todayDate: string
 * }} args
 * @returns {{
 *   matrix: Object|null,
 *   campaignFactor: number,
 *   campaignLabel: string|null,
 *   source: string
 * }}
 */
function resolveActiveMatrix(args) {
  const overrides = (args && args.productOverrides) || null;
  const todayDate = (args && args.todayDate) || '';
  const active = findActiveCampaign(overrides && overrides.campaigns, todayDate);

  // 1. Active matrix campaign replaces the base matrix entirely. An explicit
  // campaign matrix is authoritative, so it is never considered "defaulted".
  if (active && active.type === 'matrix' && active.price_matrix && typeof active.price_matrix === 'object') {
    return {
      matrix: active.price_matrix,
      campaignFactor: 1.0,
      campaignLabel: active.label || null,
      source: 'campaign:matrix:' + (active.label || 'unnamed'),
      defaulted: false,
    };
  }

  // 2 & 3. Base matrix from product override or named price list.
  const base = resolveBaseMatrix({
    pricingConfig: args && args.pricingConfig,
    productOverrides: overrides,
    priceListName: args && args.priceListName,
  });

  // Discount campaign overlays a multiplier on top of the base matrix.
  let campaignFactor = 1.0;
  let campaignLabel = null;
  if (active && active.type === 'discount') {
    const pct = parseFloat(active.discount_percent);
    if (Number.isFinite(pct) && pct > 0 && pct < 100) {
      campaignFactor = 1 - pct / 100;
      campaignLabel = active.label || null;
    }
  }

  return {
    matrix: base.matrix,
    campaignFactor: campaignFactor,
    campaignLabel: campaignLabel,
    source: campaignLabel ? base.source + '+campaign:discount' : base.source,
    defaulted: base.defaulted,
  };
}

/**
 * Parse the numeric carat from a weight string ("2.22 Cts" → 2.22, "1 Ct" → 1).
 * Module-private: only lookupStoneCents (per-carat branch) uses it, so it is NOT
 * added to scripts/sync-pricing.js EXPORTED_NAMES.
 * @param {string|number|null|undefined} weight
 * @returns {number|null} positive carat, or null if unparseable / non-positive
 */
function parseCaratWeight(weight) {
  const n = parseFloat(String(weight == null ? '' : weight));
  return (Number.isFinite(n) && n > 0) ? n : null;
}

/**
 * Look up stone price (cents) for a given weight/color/clarity.
 * Two list shapes:
 *   - absolute matrix (certified / non-certified): matrix[weight][color][clarity] = cents
 *   - per-carat (loose): { type:"per_carat", rates:{ [color]:{ [clarity]: centsPerCarat }}}
 *     → round(centsPerCarat × carat), carat parsed from the weight string.
 * @param {Object|null} matrix
 * @param {string} weight
 * @param {string} color
 * @param {string} clarity
 * @returns {number|null}
 */
function lookupStoneCents(matrix, weight, color, clarity) {
  if (!matrix) return null;

  // Per-carat (loose): rate (cents/ct) × actual carat weight.
  if (matrix.type === 'per_carat') {
    const carat = parseCaratWeight(weight);
    if (carat === null) return null;
    const rates = matrix.rates;
    if (!rates) return null;
    const byColor = rates[color];
    if (!byColor) return null;
    const rate = byColor[clarity];
    return Number.isFinite(rate) ? Math.round(rate * carat) : null;
  }

  // Absolute matrix (certified / non-certified) — unchanged.
  const w = matrix[weight];
  if (!w) return null;
  const c = w[color];
  if (!c) return null;
  const cents = c[clarity];
  return Number.isFinite(cents) ? cents : null;
}

/**
 * Sale price (cents) = round((variant + stone) × campaignFactor).
 * Shipping is added by the caller AFTER this — campaign discount must not apply to shipping.
 * @param {number} variantCents
 * @param {number} stoneCents
 * @param {number} campaignFactor 1.0 = no discount
 * @returns {number}
 */
function computeFinalCents(variantCents, stoneCents, campaignFactor) {
  const f = (campaignFactor === undefined || campaignFactor === null) ? 1.0 : campaignFactor;
  return Math.round((variantCents + stoneCents) * f);
}

/**
 * Round a cent value DOWN to the nearest dollar amount ending in 9 (e.g. 1234¢ → 1200¢ → $19, $29...).
 * Matches assets/diamond-customizer.js:1219-1220.
 * @param {number} cents
 * @returns {number}
 */
function roundCompareToNineCents(cents) {
  const dollars = cents / 100;
  const roundedToNine = Math.floor(dollars / 10) * 10 + 9;
  return roundedToNine * 100;
}

/**
 * Compare-at price (cents) — strikethrough price. Returns null if no strike should show.
 *
 * Mirrors assets/diamond-customizer.js:computeCompareAtPrice (lines 1046-1072):
 *   - shopDiscountPct > 0: mark up (variant + stone) by 100 / (100 - pct), round to $X9
 *   - else if campaign active: strike = un-discounted (variant + stone)
 *   - else: null
 * Always >= finalCents + 100 cents (minimum $1 strike gap).
 *
 * @param {{
 *   variantCents: number,
 *   stoneCents: number,
 *   shopDiscountPct?: number,
 *   campaignFactor?: number,
 *   finalCents: number
 * }} args
 * @returns {number|null}
 */
function computeCompareAtCents(args) {
  const variantCents = args.variantCents;
  const stoneCents = args.stoneCents;
  const finalCents = args.finalCents;
  const shopDiscountPct = args.shopDiscountPct || 0;
  const campaignFactor = (args.campaignFactor === undefined || args.campaignFactor === null) ? 1.0 : args.campaignFactor;

  // matrixSubtotal is the un-discounted (variant + stone) — what the strike should be calculated FROM.
  const matrixSubtotal = variantCents + stoneCents;

  if (shopDiscountPct > 0) {
    const discountMultiplier = 100 / (100 - shopDiscountPct);
    const rawCompareCents = Math.round(matrixSubtotal * discountMultiplier);
    const rounded = roundCompareToNineCents(rawCompareCents);
    return Math.max(rounded, finalCents + 100);
  }

  if (campaignFactor !== 1.0) {
    return Math.max(matrixSubtotal, finalCents + 100);
  }

  return null;
}

/* ============================================================================
 * EXPRESS-SHIPPING METAL BUCKETS  —  defect 23, metal-vocabulary inventory
 * 2026-08-19 (docs/METAL-VOCABULARY-STANDARD-2026-08-19.md §5, row 23).
 *
 * This is a SECOND, narrower metal vocabulary: the five frozen storefront
 * display names (docs/METAL-NAMING-FROZEN.md §1) collapse into three upcharge
 * buckets — Gold / Silver / Platinum — and those bucket names key the express
 * shipping surcharge table.
 *
 * It used to be a loose substring ladder:
 *     if (indexOf('Platinum') >= 0) 'Platinum'
 *     else if (indexOf('Silver') >= 0) 'Silver'
 *     else 'Gold'
 * so ANY name that was not literally spelled "Platinum"/"Silver" — a rename, a
 * new metal, a typo, "Silvadium" — fell through to the Gold bucket silently and
 * charged the wrong express shipping. The mapping below is explicit and
 * exhaustive instead, and an unrecognised metal is reported rather than
 * absorbed.
 *
 * DO NOT re-introduce substring matching here.
 * ========================================================================== */

/**
 * The frozen, EXHAUSTIVE display-name -> express-bucket table.
 * Exactly the five names in docs/METAL-NAMING-FROZEN.md §1 — no more, no less.
 * A new metal must be added here deliberately; it cannot arrive by accident.
 * @returns {Object<string,'Gold'|'Silver'|'Platinum'>} a fresh copy each call
 */
function expressMetalBuckets() {
  return {
    'Yellow Gold': 'Gold',
    'White Gold': 'Gold',
    'Rose Gold': 'Gold',
    'Platinum': 'Platinum',
    'Silver': 'Silver',
  };
}

/**
 * Bucket used when a product carries NO metal at all (metafield absent/empty).
 * This is the historical behaviour and is DELIBERATE, not a fallback for
 * unknown names: `custom.metal` defaults to 'Yellow Gold' everywhere in the
 * theme, so "no metal" means "an ordinary gold mounting".
 * @returns {'Gold'}
 */
function expressBucketWhenMetalAbsent() {
  return 'Gold';
}

/**
 * Bucket a caller that MUST NOT fail uses for an unrecognised metal. It is the
 * same bucket the old substring ladder produced, so nothing is re-priced by the
 * refactor itself — but `known:false` comes with it, and every such caller is
 * required to log. See resolveExpressUpchargePercent().
 * @returns {'Gold'}
 */
function expressBucketWhenMetalUnknown() {
  return 'Gold';
}

/**
 * Classify a storefront metal display name into its express-shipping bucket.
 *
 * Returns a RESULT rather than throwing, because the caller that matters is the
 * cart-transform Shopify Function: a throw there aborts the whole transform and
 * every customizer line reverts to the bare mounting price — an undercharge of
 * hundreds of dollars per line, which is far worse than a wrong express bucket.
 * getMetalType() below is the strict, throwing variant for callers that can
 * afford to fail.
 *
 * @param {string|null|undefined} metalName a frozen display name, e.g. 'Rose Gold'
 * @returns {{bucket:'Gold'|'Silver'|'Platinum', known:boolean, absent:boolean, input:string}}
 *   known=false means `input` is not one of the five frozen names. `bucket` is
 *   still populated (see expressBucketWhenMetalUnknown) so a fail-safe caller
 *   keeps today's price, but it MUST surface the miss.
 */
function classifyMetalForExpress(metalName) {
  var raw = (metalName === null || metalName === undefined) ? '' : String(metalName);
  var key = raw.trim();
  if (key === '') {
    return { bucket: expressBucketWhenMetalAbsent(), known: true, absent: true, input: '' };
  }
  var table = expressMetalBuckets();
  if (Object.prototype.hasOwnProperty.call(table, key)) {
    return { bucket: table[key], known: true, absent: false, input: key };
  }
  return { bucket: expressBucketWhenMetalUnknown(), known: false, absent: false, input: key };
}

/**
 * STRICT classification. Throws on anything that is not one of the five frozen
 * display names (an absent metal is still allowed — see above). Use this
 * wherever a hard failure is survivable; use classifyMetalForExpress() or
 * resolveExpressUpchargePercent() inside the cart-transform Function.
 * @param {string|null|undefined} metalName
 * @returns {'Gold'|'Silver'|'Platinum'}
 * @throws {Error} when metalName is a non-empty, unrecognised metal
 */
function getMetalType(metalName) {
  var c = classifyMetalForExpress(metalName);
  if (!c.known) {
    throw new Error(
      'getMetalType: unknown metal "' + c.input + '". The express-shipping buckets are ' +
      'exhaustive over the five frozen storefront metals (docs/METAL-NAMING-FROZEN.md §1). ' +
      'Add it to expressMetalBuckets() in shared/pricing-core.js deliberately — do not let it ' +
      'default into the Gold bucket.'
    );
  }
  return c.bucket;
}

/**
 * Resolve the express-shipping upcharge percentage for a metal, without ever
 * throwing. The `known` flag is the caller's cue to log loudly.
 *
 * @param {Object|null|undefined} upcharges { Gold:number, Silver:number, Platinum:number }
 * @param {string|null|undefined} metalName
 * @returns {{percentage:number, bucket:string, known:boolean, absent:boolean, input:string}}
 */
function resolveExpressUpchargePercent(upcharges, metalName) {
  var c = classifyMetalForExpress(metalName);
  var table = upcharges || {};
  var pct = table[c.bucket];
  return {
    percentage: (typeof pct === 'number' && isFinite(pct)) ? pct : 0,
    bucket: c.bucket,
    known: c.known,
    absent: c.absent,
    input: c.input,
  };
}

/**
 * Express shipping surcharge in cents — rounded to nearest $X9 ending.
 * Mirrors shopify-functions/.../cart_transform_run.js:calculateExpressSurcharge
 * AND assets/diamond-customizer.js:calcExpressShippingCost.
 * @param {number} variantCents
 * @param {number} percentage e.g. 12, 20, 30
 * @returns {number}
 */
function calculateExpressSurchargeCents(variantCents, percentage) {
  const surchargeRaw = variantCents * (percentage / 100);
  const surchargeDollars = surchargeRaw / 100;
  const roundedDollars = Math.floor(surchargeDollars / 10) * 10 + 9;
  return roundedDollars * 100;
}

/**
 * Detect Express vs Regular shipping selection.
 * Matches both "Express", "Express ", and "Express-Foo" patterns used across the codebase.
 * @param {string|null|undefined} shippingValue
 * @returns {boolean}
 */
function isExpressShipping(shippingValue) {
  if (!shippingValue) return false;
  return /^Express(\s|$|-)/i.test(shippingValue);
}

/* ============================================================================
 * product_config evaluator (data-driven customizer)
 * See docs/superpowers/specs/2026-06-18-customizer-redesign-design.md
 *
 * Pricing law (all 3 runtimes):
 *   total(sel) = variantCents + ( evaluateConfigCents(cfg, sel)
 *                                 − evaluateConfigCents(cfg, cfg.pricing.default) )
 * `prices` are ABSOLUTE cents; `default` is the base config baked into variant.price.
 * ========================================================================== */

/**
 * Price a selection under one pricing spec (absolute cents). Module-private —
 * evaluateConfigCents and the additive branch call it.
 * @param {Object|null} pricing   config.pricing (or an additive component spec)
 * @param {Object} selection      { [axisId]: value }
 * @returns {number|null}         absolute cents, or null if the combo isn't priced
 */
function evaluatePricingSpec(pricing, selection) {
  if (!pricing || typeof pricing !== 'object') return null;
  const sel = selection || {};
  const strategy = pricing.strategy;

  // fixed: no price-driving axes — every selection equals the base ⇒ 0.
  if (strategy === 'fixed') return 0;

  // matrix: walk pricing.prices over the `over` axes (in order) to a leaf cents.
  if (strategy === 'matrix') {
    const over = pricing.over;
    if (!Array.isArray(over) || !pricing.prices) return null;
    let node = pricing.prices;
    for (let i = 0; i < over.length; i++) {
      if (!node || typeof node !== 'object') return null;
      node = node[sel[over[i]]];
    }
    return Number.isFinite(node) ? node : null;
  }

  // per_carat: rate (lookup over the non-carat `over` axes) × parsed carat.
  if (strategy === 'per_carat') {
    const over = pricing.over || [];
    if (!pricing.prices || !pricing.caratAxis) return null;
    let node = pricing.prices;
    for (let i = 0; i < over.length; i++) {
      if (!node || typeof node !== 'object') return null;
      node = node[sel[over[i]]];
    }
    if (!Number.isFinite(node)) return null;
    const carat = parseCaratWeight(sel[pricing.caratAxis]);
    if (carat === null) return null;
    return Math.round(node * carat);
  }

  // additive: Σ of component specs (multi-stone / priced accents). Designed now,
  // exercised when a product needs it (spec §10). Any null component ⇒ null.
  if (strategy === 'additive') {
    const components = pricing.components;
    if (!Array.isArray(components)) return null;
    let total = 0;
    for (let i = 0; i < components.length; i++) {
      const c = evaluatePricingSpec(components[i], sel);
      if (c === null) return null;
      total += c;
    }
    return total;
  }

  return null; // unknown strategy
}

/**
 * Absolute stone price (cents) for a selection under a resolved product_config.
 * @param {Object|null} config     resolved product_config (with .pricing)
 * @param {Object} selection       { [axisId]: value }
 * @returns {number|null}
 */
function evaluateConfigCents(config, selection) {
  if (!config || typeof config !== 'object') return null;
  return evaluatePricingSpec(config.pricing, selection);
}

/**
 * Final sellable price (cents) via the delta law:
 *   variantCents + ( price(selection) − price(default) ).
 * base/fixed selections ⇒ delta 0 ⇒ variantCents. Returns null only when the
 * selection (or the declared default) is unpriced.
 * @param {Object|null} config
 * @param {Object} selection
 * @param {number} variantCents    Shopify variant price (= mountingCents + price(default))
 * @returns {number|null}
 */
function priceConfigSelectionCents(config, selection, variantCents) {
  if (!config || !config.pricing || !Number.isFinite(variantCents)) return null;
  const sel = evaluateConfigCents(config, selection);
  if (sel === null) return null;
  const base = evaluateConfigCents(config, config.pricing.default);
  if (base === null) return null;
  return variantCents + (sel - base);
}

/**
 * Resolve a config's pricing.prices from the shop-level priceListV2 store when the
 * product did NOT embed its own rates. Precedence: an embedded `pricing.prices`
 * ALWAYS wins (per-product override); only when it is absent do we look up the
 * shared rates via `pricing.v2 = { list, mode }` in priceListV2[list][mode].
 *   - per_carat mode stores { rates: color→clarity→cents/ct } ⇒ prices = .rates
 *   - certified / loose modes store the absolute grid directly ⇒ prices = the grid
 * Returns the SAME config object when nothing to fill (embedded present, no ref,
 * or list/mode missing) so it is a guaranteed no-op for every legacy/embedded
 * product. Pure + never throws. Each runtime calls this once after loading a
 * product_config and before evaluateConfigCents.
 * @param {Object|null} config        a product_config (with .pricing)
 * @param {Object|null} priceListV2   shop.pricing_config.priceListV2 (or null)
 * @returns {Object|null}             config with pricing.prices filled, or unchanged
 */
function resolveConfigPricesFromV2(config, priceListV2) {
  if (!config || typeof config !== 'object') return config;
  const pricing = config.pricing;
  if (!pricing || typeof pricing !== 'object') return config;
  if (pricing.prices) return config; // embedded rates win — no-op
  const ref = pricing.v2;
  if (!ref || !ref.list || !ref.mode || !priceListV2) return config;
  const entry = priceListV2[ref.list];
  if (!entry || typeof entry !== 'object') return config;
  const modeData = entry[ref.mode];
  if (!modeData || typeof modeData !== 'object') return config;
  const prices = ref.mode === 'per_carat' ? (modeData.rates || null) : modeData;
  if (!prices || typeof prices !== 'object') return config;
  return { ...config, pricing: Object.assign({}, pricing, { prices: prices }) };
}

/**
 * Resolve still-selectable values per axis given a partial selection, by applying
 * config.availability rules. A rule applies only when EVERY axis in its `when` is
 * present in `selection` AND equals the rule value; an applied rule intersects each
 * `allow` axis's current set with the allowed values.
 * @param {Object|null} config
 * @param {Object} partialSelection   { [axisId]: value } (may be incomplete)
 * @returns {Object}                  { [axisId]: allowedValues[] } for every axis
 */
function resolveAvailable(config, partialSelection) {
  const out = {};
  if (!config || !Array.isArray(config.axes)) return out;
  const sel = partialSelection || {};

  for (let i = 0; i < config.axes.length; i++) {
    const axis = config.axes[i];
    if (axis && axis.id) out[axis.id] = Array.isArray(axis.values) ? axis.values.slice() : [];
  }

  const rules = Array.isArray(config.availability) ? config.availability : [];
  for (let r = 0; r < rules.length; r++) {
    const rule = rules[r];
    if (!rule || typeof rule !== 'object') continue;
    const when = rule.when || {};
    const allow = rule.allow || {};

    let applies = true;
    const whenKeys = Object.keys(when);
    for (let k = 0; k < whenKeys.length; k++) {
      const key = whenKeys[k];
      if (sel[key] !== when[key]) { applies = false; break; }
    }
    if (!applies) continue;

    const allowKeys = Object.keys(allow);
    for (let k = 0; k < allowKeys.length; k++) {
      const axisId = allowKeys[k];
      const permitted = allow[axisId];
      if (!Array.isArray(permitted) || !Array.isArray(out[axisId])) continue;
      out[axisId] = out[axisId].filter(function (v) { return permitted.indexOf(v) >= 0; });
    }
  }

  return out;
}

// === SYNC FOOTER (auto-generated by scripts/sync-pricing.js — do not edit) ===
if (typeof window !== 'undefined') {
  window.BCPricing = {
    dateOnly,
    findActiveCampaign,
    resolveBaseMatrix,
    resolveActiveMatrix,
    lookupStoneCents,
    computeFinalCents,
    roundCompareToNineCents,
    computeCompareAtCents,
    expressMetalBuckets,
    expressBucketWhenMetalAbsent,
    expressBucketWhenMetalUnknown,
    classifyMetalForExpress,
    getMetalType,
    resolveExpressUpchargePercent,
    calculateExpressSurchargeCents,
    isExpressShipping,
    evaluateConfigCents,
    priceConfigSelectionCents,
    resolveConfigPricesFromV2,
    resolveAvailable
  };
}
